import { useEffect, useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import { Button, Stack, Typography } from '@mui/material';

import { ReportTierOptions, TIER_DESCRIPTION } from '@StandingAccess/types';
import { useRemediatedAccessFlows } from '@StandingAccess/services/standingAccessQueries';
import AssessmentScoreWidget from '@StandingAccess/components/AssessmentScoreWidget';
import { MaterialIcon } from '@components';
import AssessmentRemediationWidget, {
  AssessmentRemediationWidgetStep,
} from '@StandingAccess/components/AssessmentRemediationWidget';
import AppModal from '@common/ui/AppModal';
import { RouterLink } from '@libs/router';
import { routes } from '@routes';
import { AccessFlowAppModelV2, StandingAccessReportAppModel } from '@api';
import useReportScore, { getTierSummary } from '@StandingAccess/hooks/use-report-score';
import LoadingButton from '@mui/lab/LoadingButton';

interface RemediationModalProps {
  report: StandingAccessReportAppModel;
  tierToRemediate?: ReportTierOptions;
  onClose: () => void;
}

export default function AssessmentReportRemediationModal({ report, tierToRemediate, onClose }: RemediationModalProps) {
  const { score } = useReportScore({ report });
  const { remediatedAccessFlows, isFetched, refetch } = useRemediatedAccessFlows({ reportId: report.id });

  useEffect(() => {
    if (tierToRemediate) {
      refetch();
    }
  }, [refetch, tierToRemediate]);

  const critical = getTierSummary(report, ReportTierOptions.critical);
  const high = getTierSummary(report, ReportTierOptions.high);
  const medium = getTierSummary(report, ReportTierOptions.medium);
  const low = getTierSummary(report, ReportTierOptions.low);

  return (
    <AppModal header={<RemediationModalHeader />} open={!!tierToRemediate} onClose={onClose} width={800}>
      <Stack direction="column" justifyContent="center" spacing={2}>
        <AssessmentScoreWidget value={score} size={45} direction="row" />
        <AssessmentRemediationWidget
          type={ReportTierOptions.critical}
          description={TIER_DESCRIPTION[ReportTierOptions.critical]}
          step1={
            <RemediationModalAccessFlowStep
              tier={ReportTierOptions.critical}
              reportId={report.id}
              accessFlow={remediatedAccessFlows[ReportTierOptions.critical]}
              isLoading={!isFetched}
            />
          }
          step2={
            <RemediationModalRevokeStep
              count={critical.iras - critical.iras_revoked}
              tier={ReportTierOptions.critical}
              reportId={report.id}
            />
          }
          hideSteps={tierToRemediate !== ReportTierOptions.critical}
        />
        <AssessmentRemediationWidget
          type={ReportTierOptions.high}
          description={TIER_DESCRIPTION[ReportTierOptions.high]}
          step1={
            <RemediationModalAccessFlowStep
              tier={ReportTierOptions.high}
              reportId={report.id}
              accessFlow={remediatedAccessFlows[ReportTierOptions.high]}
              isLoading={!isFetched}
            />
          }
          step2={
            <RemediationModalRevokeStep
              count={high.iras - high.iras_revoked}
              tier={ReportTierOptions.high}
              reportId={report.id}
            />
          }
          hideSteps={tierToRemediate !== ReportTierOptions.high}
        />
        <AssessmentRemediationWidget
          type={ReportTierOptions.medium}
          description={TIER_DESCRIPTION[ReportTierOptions.medium]}
          step1={
            <RemediationModalAccessFlowStep
              tier={ReportTierOptions.medium}
              reportId={report.id}
              accessFlow={remediatedAccessFlows[ReportTierOptions.medium]}
              isLoading={!isFetched}
            />
          }
          step2={
            <RemediationModalRevokeStep
              count={medium.iras - medium.iras_revoked}
              tier={ReportTierOptions.medium}
              reportId={report.id}
            />
          }
          hideSteps={tierToRemediate !== ReportTierOptions.medium}
        />
        <AssessmentRemediationWidget
          type={ReportTierOptions.low}
          description={TIER_DESCRIPTION[ReportTierOptions.low]}
          step1={
            <RemediationModalAccessFlowStep
              tier={ReportTierOptions.low}
              reportId={report.id}
              accessFlow={remediatedAccessFlows[ReportTierOptions.low]}
              isLoading={!isFetched}
            />
          }
          step2={
            <RemediationModalRevokeStep
              count={low.iras - low.iras_revoked}
              tier={ReportTierOptions.low}
              reportId={report.id}
            />
          }
          hideSteps={tierToRemediate !== ReportTierOptions.low}
        />
      </Stack>
    </AppModal>
  );
}

function RemediationModalRevokeStep({
  count,
  tier,
  reportId,
}: {
  count: number;
  tier: ReportTierOptions;
  reportId: string;
}) {
  return (
    <AssessmentRemediationWidgetStep
      step={2}
      text={`Revoke standing access to ${count} assignments`}
      action={
        <Button
          variant="outlined"
          size="small"
          endIcon={<MaterialIcon symbol="arrow_right_alt" />}
          fullWidth
          component={RouterLink}
          to={generatePath(routes.StandingAccessReportRevoke.path, {
            id: reportId,
            tier,
          })}
          disabled={count === 0}
        >
          Revoke standing access
        </Button>
      }
    />
  );
}

function RemediationModalAccessFlowStep({
  tier,
  reportId,
  accessFlow,
  isLoading,
}: {
  tier: ReportTierOptions;
  reportId: string;
  accessFlow?: AccessFlowAppModelV2;
  isLoading?: boolean;
}) {
  const buttonPath = useMemo(
    () =>
      accessFlow
        ? generatePath(routes.EditAccessFlow.path, {
            id: accessFlow.id,
          })
        : generatePath(routes.StandingAccessReportAccessFlow.path, {
            id: reportId,
            tier,
          }),
    [accessFlow, reportId, tier],
  );

  const hasAccessFlow = !!accessFlow;

  const action = (
    <LoadingButton variant="outlined" size="small" fullWidth component={RouterLink} to={buttonPath} loading={isLoading}>
      {hasAccessFlow ? 'View Access Flow' : 'Create Access Flow'}
    </LoadingButton>
  );

  return (
    <AssessmentRemediationWidgetStep step={1} text="Create Access Flow" finished={hasAccessFlow} action={action} />
  );
}

function RemediationModalHeader() {
  return (
    <Stack direction="column" justifyContent="center" alignItems="flex-start">
      <Typography variant="subtitle1">Remediation plan</Typography>
      <Typography variant="helperText" color="text.muted">
        Implement recommendations to improve access posture
      </Typography>
    </Stack>
  );
}
